import { useState } from "react"
import { SlidersHorizontal, X } from "lucide-react"

import { Button } from "@/components/ui/button"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet"
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group"
import useMediaQuery from "@/hooks/useMediaQuery"
import { BarButton, CountBadge } from "./FilterBarButton"

const RATING_CHOICES = [0, 4, 4.5]
const RELIABILITY_CHOICES = [0, 90, 95]

// A link saved while these were sliders can carry any step ("3.5", "85"). Keep
// that value exactly and show it as one more choice, so the results still
// match the link and the control shows what is applied.
export const withLegacy = (choices, value) =>
  !value || choices.includes(value) ? choices : [...choices, value].sort((a, b) => a - b)

function Section({ label, children }) {
  return (
    <div className="border-t border-border px-[16px] py-[14px] first:border-t-0">
      <div className="mb-[10px] text-[11px] font-bold tracking-[0.06em] text-ink-3 uppercase">{label}</div>
      {children}
    </div>
  )
}

// A single-choice group never goes empty: Radix reports "" when the pressed
// segment is pressed again, which here means "leave it as it is".
function SingleChoice({ label, value, choices, format, onPick }) {
  return (
    <Section label={label}>
      <ToggleGroup
        type="single"
        aria-label={label}
        value={String(value)}
        onValueChange={(v) => v !== "" && onPick(v)}
      >
        {choices.map((c) => (
          <ToggleGroupItem key={c} value={String(c)}>
            {format(c)}
          </ToggleGroupItem>
        ))}
      </ToggleGroup>
    </Section>
  )
}

function Body({ facets, applied, onChange }) {
  const tiers = [{ key: "all", label: "Any" }, ...facets.tiers]
  return (
    <>
      <SingleChoice
        label="Tier"
        value={applied.tier || "all"}
        choices={tiers.map((t) => t.key)}
        format={(k) => tiers.find((t) => t.key === k)?.label}
        onPick={(v) => onChange({ tier: v === "all" ? null : v })}
      />
      <SingleChoice
        label="Minimum rating"
        value={applied.minRating || 0}
        choices={withLegacy(RATING_CHOICES, applied.minRating)}
        format={(v) => (v ? `${v}+` : "Any")}
        onPick={(v) => onChange({ minRating: Number(v) })}
      />
      <SingleChoice
        label="Reliability score"
        value={applied.minReliability || 0}
        choices={withLegacy(RELIABILITY_CHOICES, applied.minReliability)}
        format={(v) => (v ? `${v}%+` : "Any")}
        onPick={(v) => onChange({ minReliability: Number(v) })}
      />
      <Section label="Language">
        <ToggleGroup
          type="multiple"
          aria-label="Language"
          value={applied.languages ?? []}
          onValueChange={(list) => onChange({ languages: list })}
        >
          {facets.languages.map((lang) => (
            <ToggleGroupItem key={lang} value={lang}>
              {lang}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
      </Section>
    </>
  )
}

// Tier, rating, reliability and language: the filters most searches never
// touch. A popover on desktop; at 860px and below the same controls slide in
// from the right with a "Show N freelancers" footer.
export default function MoreFilters({ facets, applied, total, onChange }) {
  const narrow = useMediaQuery("(max-width: 860px)")
  const [open, setOpen] = useState(false)
  const count =
    (applied.tier ? 1 : 0) + (applied.minRating ? 1 : 0) + (applied.minReliability ? 1 : 0) +
    (applied.languages?.length || 0)
  const reset = () => onChange({ tier: null, minRating: 0, minReliability: 0, languages: [] })

  const trigger = (
    <BarButton has={count > 0} chevron={false}>
      <SlidersHorizontal className="size-[15px] shrink-0 text-ink-3" strokeWidth={2} />
      <span className="max-[561px]:hidden">More filters</span>
      <span className="hidden max-[561px]:inline">Filters</span>
      {count > 0 && <CountBadge n={count} />}
    </BarButton>
  )

  const header = (title, close) => (
    <div className="flex items-center gap-[12px] border-b border-border px-[16px] py-[13px]">
      {title}
      {count > 0 && (
        <button type="button" onClick={reset} className="text-[12px] font-semibold text-muted-foreground hover:text-foreground">
          Reset
        </button>
      )}
      {close}
    </div>
  )

  if (narrow) {
    return (
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetTrigger asChild>{trigger}</SheetTrigger>
        <SheetContent side="right" showCloseButton={false} className="w-[min(380px,92vw)] gap-0 p-0 sm:max-w-none">
          {header(
            <SheetTitle className="mr-auto font-display text-[14px] font-bold">More filters</SheetTitle>,
            <SheetClose asChild>
              <button
                type="button"
                aria-label="Close"
                className="flex size-[34px] items-center justify-center rounded-[9px] border border-border bg-card text-muted-foreground"
              >
                <X className="size-4" strokeWidth={2} />
              </button>
            </SheetClose>
          )}
          <SheetDescription className="sr-only">
            Narrow the roster by tier, rating, reliability and language.
          </SheetDescription>
          <div className="min-h-0 flex-1 overflow-y-auto">
            <Body facets={facets} applied={applied} onChange={onChange} />
          </div>
          <div className="border-t border-border p-[14px_16px]">
            <SheetClose asChild>
              <Button className="w-full justify-center">
                Show {total} freelancer{total !== 1 ? "s" : ""}
              </Button>
            </SheetClose>
          </div>
        </SheetContent>
      </Sheet>
    )
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={7}
        collisionPadding={14}
        aria-label="More filters"
        className="w-[340px] rounded-[13px] border-border bg-card p-0 shadow-[0_20px_46px_-16px_rgba(0,0,0,0.34)]"
      >
        {header(<span className="mr-auto font-display text-[14px] font-bold">More filters</span>, null)}
        <Body facets={facets} applied={applied} onChange={onChange} />
      </PopoverContent>
    </Popover>
  )
}
